import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

/**
 * iOS Safari install instructions modal.
 * Shows step-by-step guide: Share → Add to Home Screen.
 * Receives state from PWAInstallPrompt parent.
 */

// Inline iOS Share icon (matches SF Symbols share.up)
function ShareIcon({ className }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden="true"
        >
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
        </svg>
    )
}

// Inline plus-square icon for "Add to Home Screen"
function PlusSquareIcon({ className }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden="true"
        >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
    )
}

export default function IOSInstallModal({ showIOSModal, dismiss, dismissPermanent }) {
    const { t } = useTranslation()

    return (
        <AnimatePresence>
            {showIOSModal && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="ios-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
                        onClick={dismiss}
                        aria-hidden="true"
                    />

                    {/* Modal card */}
                    <motion.div
                        key="ios-modal"
                        initial={{ y: '100%', opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: '100%', opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 350, damping: 32 }}
                        className="fixed bottom-0 left-0 right-0 z-[9999] mx-auto max-w-md
                                   rounded-t-3xl border-t border-primary/20 bg-sable
                                   shadow-[0_-8px_40px_rgba(0,0,0,0.55)]
                                   pb-[env(safe-area-inset-bottom,16px)]"
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('pwa.ios_title')}
                        id="ios-install-modal"
                    >
                        {/* Drag handle */}
                        <div className="flex justify-center pt-3 pb-2">
                            <div className="h-1 w-10 rounded-full bg-white/20" />
                        </div>

                        {/* Header */}
                        <div className="flex items-center gap-3.5 px-6 pb-4">
                            <img
                                src="/logo.webp"
                                alt="JD Rivero"
                                width={52}
                                height={52}
                                className="h-13 w-13 rounded-2xl border border-white/10 bg-black object-contain p-1.5 flex-shrink-0"
                            />
                            <div>
                                <p className="text-base font-semibold text-cloud tracking-wide">
                                    {t('pwa.ios_title')}
                                </p>
                                <p className="mt-0.5 text-xs text-cloud/50 leading-snug">
                                    {t('pwa.ios_desc')}
                                </p>
                            </div>
                        </div>

                        {/* Steps */}
                        <div className="mx-6 space-y-4 rounded-2xl bg-white/5 p-5">
                            {/* Step 1 */}
                            <div className="flex items-start gap-3">
                                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                                    1
                                </span>
                                <div className="pt-0.5">
                                    <p className="text-sm font-medium text-cloud">
                                        {t('pwa.ios_step1')}{' '}
                                        <ShareIcon className="inline-block h-4 w-4 align-text-bottom text-primary" />
                                        {' '}<span className="font-semibold text-primary">{t('pwa.ios_share')}</span>
                                    </p>
                                    <p className="mt-0.5 text-xs text-cloud/40">
                                        {t('pwa.ios_step1_hint')}
                                    </p>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="border-t border-white/5" />

                            {/* Step 2 */}
                            <div className="flex items-start gap-3">
                                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                                    2
                                </span>
                                <div className="pt-0.5">
                                    <p className="text-sm font-medium text-cloud">
                                        {t('pwa.ios_step2')}{' '}
                                        <PlusSquareIcon className="inline-block h-4 w-4 align-text-bottom text-primary" />
                                        {' '}<span className="font-semibold text-primary">{t('pwa.ios_add_home')}</span>
                                    </p>
                                    <p className="mt-0.5 text-xs text-cloud/40">
                                        {t('pwa.ios_step2_hint')}{' '}
                                        <span className="font-medium text-cloud/60">{t('pwa.ios_step2_hint_add')}</span>{' '}
                                        {t('pwa.ios_step2_hint_suffix')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 px-6 pt-5 pb-2">
                            <button
                                onClick={dismiss}
                                className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-3
                                           text-sm font-semibold text-cloud/80 transition-all duration-200
                                           hover:bg-white/10 active:scale-[0.97]"
                                id="ios-dismiss-btn"
                            >
                                {t('pwa.ios_maybe_later')}
                            </button>
                        </div>

                        {/* Permanent dismiss */}
                        <div className="px-6 pb-3 pt-1 text-center">
                            <button
                                onClick={dismissPermanent}
                                className="text-[11px] text-cloud/25 transition-colors hover:text-cloud/45"
                                id="ios-dismiss-permanent-btn"
                            >
                                {t('pwa.ios_dont_show')}
                            </button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
